import {
  DataType,
  Role,
  TransferMode,
  SourcePocReference,
  VerificationStatus,
  MESSAGE_DICTIONARY_VERSION,
  type MessageDefinition,
  type MessageFieldDefinition,
} from "@k-ucap/shared";
import {
  OperatingMode,
  OperatingState,
  AlertLevel,
} from "../enums/stanag4586.js";

const now = new Date().toISOString();

const pocMetadata = {
  source: SourcePocReference.PUBLIC_POC_REFERENCE,
  official: false,
  verificationStatus: VerificationStatus.REQUIRES_MND_STD_VERIFICATION,
  notes: "공개자료 기반 PoC 정의이며 공식 표준 검증 필요",
  validationSource: "POC" as const,
};

const defaultDemoValue = (field: MessageFieldDefinition): unknown => {
  if (field.demoValue !== undefined) return field.demoValue;
  if (field.dataType === DataType.BOOLEAN) return false;
  if (field.dataType === DataType.STRING) return "";
  if (field.dataType === DataType.ENUM) {
    if (field.enumRef === "OperatingMode") return OperatingMode.STANDBY;
    if (field.enumRef === "OperatingState") return OperatingState.CRUISE;
    if (field.enumRef === "AlertLevel") return AlertLevel.INFO;
    return "";
  }
  if (field.dataType === DataType.BYTES) return [];
  return 0;
};

const createMessageDefinition = (
  partial: Omit<
    MessageDefinition,
    "metadata" | "createdAt" | "updatedAt" | "dictionaryVersion"
  >,
): MessageDefinition =>
  ({
    ...partial,
    dictionaryVersion: MESSAGE_DICTIONARY_VERSION,
    metadata: pocMetadata,
    createdAt: now,
    updatedAt: now,
    fields: (partial.fields as MessageFieldDefinition[]).map((field) => ({
      ...field,
      demoValue: defaultDemoValue(field as MessageFieldDefinition),
    })),
  }) as MessageDefinition;

export const messageDefinitions: MessageDefinition[] = [
  createMessageDefinition({
    id: "stanag4586-01-auth-request",
    standard: "STANAG 4586",
    edition: "PUBLIC_POC",
    messageType: 1,
    name: "Authorisation Request",
    displayName: "Authorisation Request",
    category: "AUTHORIZATION",
    description: "CUCS가 VSM에 대한 통제 권한을 요청합니다.",
    sourceRole: Role.CUCS,
    targetRole: Role.VSM,
    transferMode: TransferMode.COMMAND,
    defaultPeriodMs: 0,
    acknowledgementRequired: true,
    supported: true,
    fields: [
      {
        order: 1,
        key: "vehicleId",
        name: "Vehicle ID",
        displayName: "차량 ID",
        description: "요청 대상 Vehicle ID",
        dataType: DataType.STRING,
        required: true,
        uiControl: "text",
      },
      {
        order: 2,
        key: "cucsId",
        name: "CUCS ID",
        displayName: "CUCS ID",
        description: "요청 CUCS 식별자",
        dataType: DataType.STRING,
        required: true,
        uiControl: "text",
      },
    ],
    tags: ["STANAG4586", "AUTHORIZATION", "COMMAND"],
  }),
  createMessageDefinition({
    id: "stanag4586-21-auth-response",
    standard: "STANAG 4586",
    edition: "PUBLIC_POC",
    messageType: 21,
    name: "Authorisation Response",
    displayName: "Authorisation Response",
    category: "AUTHORIZATION",
    description: "VSM이 CUCS의 권한 요청에 응답합니다.",
    sourceRole: Role.VSM,
    targetRole: Role.CUCS,
    transferMode: TransferMode.RESPONSE,
    defaultPeriodMs: 0,
    acknowledgementRequired: false,
    supported: true,
    fields: [
      {
        order: 1,
        key: "vehicleId",
        name: "Vehicle ID",
        displayName: "차량 ID",
        description: "응답 대상 Vehicle ID",
        dataType: DataType.STRING,
        required: true,
        uiControl: "text",
      },
      {
        order: 2,
        key: "authorised",
        name: "Authorised",
        displayName: "승인 여부",
        description: "권한 요청 승인 여부",
        dataType: DataType.BOOLEAN,
        required: true,
        uiControl: "checkbox",
      },
    ],
    tags: ["STANAG4586", "AUTHORIZATION", "RESPONSE"],
  }),
  createMessageDefinition({
    id: "stanag4586-42-operating-mode-command",
    standard: "STANAG 4586",
    edition: "PUBLIC_POC",
    messageType: 42,
    name: "Operating Mode Command",
    displayName: "Operating Mode Command",
    category: "CONTROL",
    description: "운용 모드를 전환합니다.",
    sourceRole: Role.CUCS,
    targetRole: Role.VSM,
    transferMode: TransferMode.COMMAND,
    defaultPeriodMs: 0,
    acknowledgementRequired: true,
    supported: true,
    fields: [
      {
        order: 1,
        key: "vehicleId",
        name: "Vehicle ID",
        displayName: "차량 ID",
        dataType: DataType.STRING,
        required: true,
        uiControl: "text",
      },
      {
        order: 2,
        key: "operatingMode",
        name: "Operating Mode",
        displayName: "운용 모드",
        dataType: DataType.ENUM,
        required: true,
        enumRef: "OperatingMode",
        uiControl: "select",
      },
    ],
    tags: ["STANAG4586", "CONTROL", "COMMAND"],
  }),
  createMessageDefinition({
    id: "stanag4586-43-vehicle-steering-command",
    standard: "STANAG 4586",
    edition: "PUBLIC_POC",
    messageType: 43,
    name: "Vehicle Steering Command",
    displayName: "Vehicle Steering Command",
    category: "CONTROL",
    description: "기체 조향/조종 명령을 전송합니다.",
    sourceRole: Role.CUCS,
    targetRole: Role.VSM,
    transferMode: TransferMode.COMMAND,
    defaultPeriodMs: 100,
    acknowledgementRequired: true,
    supported: true,
    fields: [
      {
        order: 1,
        key: "vehicleId",
        name: "Vehicle ID",
        displayName: "차량 ID",
        dataType: DataType.STRING,
        required: true,
        uiControl: "text",
      },
      {
        order: 2,
        key: "rollDeg",
        name: "Roll",
        displayName: "롤 (deg)",
        dataType: DataType.FLOAT32,
        byteLength: 4,
        required: true,
        unit: "rad",
        displayUnit: "deg",
        uiControl: "number",
      },
      {
        order: 3,
        key: "pitchDeg",
        name: "Pitch",
        displayName: "피치 (deg)",
        dataType: DataType.FLOAT32,
        byteLength: 4,
        required: true,
        unit: "rad",
        displayUnit: "deg",
        uiControl: "number",
      },
      {
        order: 4,
        key: "yawDeg",
        name: "Yaw",
        displayName: "요 (deg)",
        dataType: DataType.FLOAT32,
        byteLength: 4,
        required: true,
        unit: "rad",
        displayUnit: "deg",
        uiControl: "number",
      },
    ],
    tags: ["STANAG4586", "CONTROL", "COMMAND"],
  }),
  createMessageDefinition({
    id: "stanag4586-101-inertial-states",
    standard: "STANAG 4586",
    edition: "PUBLIC_POC",
    messageType: 101,
    name: "Inertial States",
    displayName: "Inertial States",
    category: "NAVIGATION",
    description: "기체 관성 상태(위치, 속도, 자세)를 보고합니다.",
    sourceRole: Role.AIR_VEHICLE,
    targetRole: Role.CUCS,
    transferMode: TransferMode.PUSH,
    defaultPeriodMs: 100,
    acknowledgementRequired: false,
    supported: true,
    fields: [
      {
        order: 1,
        key: "timeStamp",
        name: "Time Stamp",
        displayName: "시간",
        dataType: DataType.UINT32,
        byteLength: 4,
        required: true,
        uiControl: "datetime",
        demoValue: Math.floor(Date.now() / 1000),
      },
      {
        order: 2,
        key: "vehicleId",
        name: "Vehicle ID",
        displayName: "차량 ID",
        dataType: DataType.STRING,
        required: true,
        uiControl: "text",
        demoValue: "UAV-001",
      },
      {
        order: 3,
        key: "cucsId",
        name: "CUCS ID",
        displayName: "CUCS ID",
        dataType: DataType.STRING,
        required: true,
        uiControl: "text",
        demoValue: "CUCS-01",
      },
      {
        order: 4,
        key: "latitude",
        name: "Latitude",
        displayName: "위도",
        dataType: DataType.FLOAT64,
        byteLength: 8,
        required: true,
        unit: "deg",
        displayUnit: "deg",
        minimum: -90,
        maximum: 90,
        uiControl: "number",
        gisMapping: { enabled: true, property: "latitude" },
        demoValue: 37.389,
      },
      {
        order: 5,
        key: "longitude",
        name: "Longitude",
        displayName: "경도",
        dataType: DataType.FLOAT64,
        byteLength: 8,
        required: true,
        unit: "deg",
        displayUnit: "deg",
        minimum: -180,
        maximum: 180,
        uiControl: "number",
        gisMapping: { enabled: true, property: "longitude" },
        demoValue: 126.647,
      },
      {
        order: 6,
        key: "altitudeM",
        name: "Altitude",
        displayName: "고도 (m)",
        dataType: DataType.FLOAT32,
        byteLength: 4,
        required: true,
        unit: "m",
        displayUnit: "m",
        uiControl: "number",
        gisMapping: { enabled: true, property: "altitude" },
        demoValue: 1200,
      },
      {
        order: 7,
        key: "northVelocityMps",
        name: "North Velocity",
        displayName: "북속 (m/s)",
        dataType: DataType.FLOAT32,
        byteLength: 4,
        required: true,
        unit: "m/s",
        displayUnit: "m/s",
        uiControl: "number",
        demoValue: 0,
      },
      {
        order: 8,
        key: "eastVelocityMps",
        name: "East Velocity",
        displayName: "동속 (m/s)",
        dataType: DataType.FLOAT32,
        byteLength: 4,
        required: true,
        unit: "m/s",
        displayUnit: "m/s",
        uiControl: "number",
        demoValue: 55,
      },
      {
        order: 9,
        key: "verticalVelocityMps",
        name: "Vertical Velocity",
        displayName: "수직속도 (m/s)",
        dataType: DataType.FLOAT32,
        byteLength: 4,
        required: true,
        unit: "m/s",
        displayUnit: "m/s",
        uiControl: "number",
        demoValue: 0,
      },
      {
        order: 10,
        key: "rollDeg",
        name: "Roll",
        displayName: "롤 (deg)",
        dataType: DataType.FLOAT32,
        byteLength: 4,
        required: true,
        unit: "rad",
        displayUnit: "deg",
        uiControl: "number",
        demoValue: 0,
      },
      {
        order: 11,
        key: "pitchDeg",
        name: "Pitch",
        displayName: "피치 (deg)",
        dataType: DataType.FLOAT32,
        byteLength: 4,
        required: true,
        unit: "rad",
        displayUnit: "deg",
        uiControl: "number",
        demoValue: 0,
      },
      {
        order: 12,
        key: "yawDeg",
        name: "Yaw",
        displayName: "요 (deg)",
        dataType: DataType.FLOAT32,
        byteLength: 4,
        required: true,
        unit: "rad",
        displayUnit: "deg",
        uiControl: "number",
        demoValue: 90,
      },
    ],
    tags: ["STANAG4586", "NAVIGATION", "PUSH", "GIS"],
  }),
  createMessageDefinition({
    id: "stanag4586-104-vehicle-operating-states",
    standard: "STANAG 4586",
    edition: "PUBLIC_POC",
    messageType: 104,
    name: "Vehicle Operating States",
    displayName: "Vehicle Operating States",
    category: "STATUS",
    description: "기체 운용 상태를 보고합니다.",
    sourceRole: Role.AIR_VEHICLE,
    targetRole: Role.CUCS,
    transferMode: TransferMode.PUSH,
    defaultPeriodMs: 1000,
    acknowledgementRequired: false,
    supported: true,
    fields: [
      {
        order: 1,
        key: "vehicleId",
        name: "Vehicle ID",
        displayName: "차량 ID",
        dataType: DataType.STRING,
        required: true,
        uiControl: "text",
      },
      {
        order: 2,
        key: "operatingState",
        name: "Operating State",
        displayName: "운용 상태",
        dataType: DataType.ENUM,
        required: true,
        enumRef: "OperatingState",
        uiControl: "select",
      },
      {
        order: 3,
        key: "fuelLevelPercent",
        name: "Fuel Level",
        displayName: "연료량 (%)",
        dataType: DataType.FLOAT32,
        byteLength: 4,
        required: false,
        minimum: 0,
        maximum: 100,
        uiControl: "number",
      },
    ],
    tags: ["STANAG4586", "STATUS", "PUSH"],
  }),
  createMessageDefinition({
    id: "stanag4586-110-from-to-next-waypoint-states",
    standard: "STANAG 4586",
    edition: "PUBLIC_POC",
    messageType: 110,
    name: "From-To-Next Waypoint States",
    displayName: "From-To-Next Waypoint States",
    category: "NAVIGATION",
    description: "현재, 이전, 다음 경유지 상태를 보고합니다.",
    sourceRole: Role.AIR_VEHICLE,
    targetRole: Role.CUCS,
    transferMode: TransferMode.PUSH,
    defaultPeriodMs: 1000,
    acknowledgementRequired: false,
    supported: true,
    fields: [
      {
        order: 1,
        key: "vehicleId",
        name: "Vehicle ID",
        displayName: "차량 ID",
        dataType: DataType.STRING,
        required: true,
        uiControl: "text",
      },
      {
        order: 2,
        key: "fromWaypointId",
        name: "From Waypoint ID",
        displayName: "이전 경유지",
        dataType: DataType.STRING,
        required: false,
        uiControl: "text",
      },
      {
        order: 3,
        key: "toWaypointId",
        name: "To Waypoint ID",
        displayName: "현재 경유지",
        dataType: DataType.STRING,
        required: true,
        uiControl: "text",
      },
      {
        order: 4,
        key: "nextWaypointId",
        name: "Next Waypoint ID",
        displayName: "다음 경유지",
        dataType: DataType.STRING,
        required: false,
        uiControl: "text",
      },
    ],
    tags: ["STANAG4586", "NAVIGATION", "PUSH", "WAYPOINT"],
  }),
  createMessageDefinition({
    id: "stanag4586-1100-subsystem-status-alert",
    standard: "STANAG 4586",
    edition: "PUBLIC_POC",
    messageType: 1100,
    name: "Subsystem Status Alert",
    displayName: "Subsystem Status Alert",
    category: "ALERT",
    description: "하위체계 상태 경고/알림을 전송합니다.",
    sourceRole: Role.VSM,
    targetRole: Role.CUCS,
    transferMode: TransferMode.EVENT,
    defaultPeriodMs: 0,
    acknowledgementRequired: true,
    supported: true,
    fields: [
      {
        order: 1,
        key: "vehicleId",
        name: "Vehicle ID",
        displayName: "차량 ID",
        dataType: DataType.STRING,
        required: true,
        uiControl: "text",
      },
      {
        order: 2,
        key: "subsystemId",
        name: "Subsystem ID",
        displayName: "하위체계 ID",
        dataType: DataType.STRING,
        required: true,
        uiControl: "text",
      },
      {
        order: 3,
        key: "alertLevel",
        name: "Alert Level",
        displayName: "경보 수준",
        dataType: DataType.ENUM,
        required: true,
        enumRef: "AlertLevel",
        uiControl: "select",
      },
      {
        order: 4,
        key: "description",
        name: "Description",
        displayName: "설명",
        dataType: DataType.STRING,
        required: false,
        uiControl: "textarea",
      },
    ],
    tags: ["STANAG4586", "ALERT", "EVENT"],
  }),
  createMessageDefinition({
    id: "stanag4586-1400-message-acknowledgement",
    standard: "STANAG 4586",
    edition: "PUBLIC_POC",
    messageType: 1400,
    name: "Message Acknowledgement",
    displayName: "Message Acknowledgement",
    category: "CONTROL",
    description: "수신 메시지에 대한 긍정/부정 확인 응답을 전송합니다.",
    sourceRole: Role.VSM,
    targetRole: Role.CUCS,
    transferMode: TransferMode.RESPONSE,
    defaultPeriodMs: 0,
    acknowledgementRequired: false,
    supported: true,
    fields: [
      {
        order: 1,
        key: "originalMessageId",
        name: "Original Message ID",
        displayName: "원본 메시지 ID",
        dataType: DataType.STRING,
        required: true,
        uiControl: "text",
      },
      {
        order: 2,
        key: "acknowledged",
        name: "Acknowledged",
        displayName: "확인 여부",
        dataType: DataType.BOOLEAN,
        required: true,
        uiControl: "checkbox",
      },
      {
        order: 3,
        key: "reasonCode",
        name: "Reason Code",
        displayName: "사유 코드",
        dataType: DataType.UINT16,
        byteLength: 2,
        required: false,
        uiControl: "number",
      },
    ],
    tags: ["STANAG4586", "CONTROL", "RESPONSE"],
  }),
  createMessageDefinition({
    id: "stanag4586-1402-schedule-message-update-command",
    standard: "STANAG 4586",
    edition: "PUBLIC_POC",
    messageType: 1402,
    name: "Schedule Message Update Command",
    displayName: "Schedule Message Update Command",
    category: "CONTROL",
    description: "메시지 송신 주기 변경 명령을 전송합니다.",
    sourceRole: Role.CUCS,
    targetRole: Role.VSM,
    transferMode: TransferMode.COMMAND,
    defaultPeriodMs: 0,
    acknowledgementRequired: true,
    supported: true,
    fields: [
      {
        order: 1,
        key: "vehicleId",
        name: "Vehicle ID",
        displayName: "차량 ID",
        dataType: DataType.STRING,
        required: true,
        uiControl: "text",
      },
      {
        order: 2,
        key: "messageType",
        name: "Message Type",
        displayName: "메시지 타입",
        dataType: DataType.UINT16,
        byteLength: 2,
        required: true,
        uiControl: "number",
      },
      {
        order: 3,
        key: "periodMs",
        name: "Period",
        displayName: "주기 (ms)",
        dataType: DataType.UINT32,
        byteLength: 4,
        required: true,
        minimum: 0,
        uiControl: "number",
      },
    ],
    tags: ["STANAG4586", "CONTROL", "COMMAND"],
  }),
];

export const getMessageDefinitionByType = (
  messageType: number,
): MessageDefinition | undefined =>
  messageDefinitions.find((m) => m.messageType === messageType);

export const getMessageDefinitions = (): MessageDefinition[] => [
  ...messageDefinitions,
];
